import React from 'react';
import _, { get } from 'lodash';
import { Tooltip } from 'antd';
import { CaretDownOutlined, CaretUpOutlined } from '@ant-design/icons';
import { formatComma } from 'helpers/helpers';

// ปีที่แล้วที่ snapshot ไว้ใน examsitehistories (tasks/exams/snapshot-site-history.js ฝั่ง API)
export const HISTORY_YEAR = '69';

// ห้อง/ที่นั่งรวมทุกประเภท (ปกติ + พิเศษ + CBT) ให้ตรงกับคอลัมน์ "ห้อง"/"ที่นั่งสอบ" ของตาราง
export const totalStat = (stat) => ({
  rooms: (get(stat, 'rooms') || 0) + (get(stat, 'special_rooms') || 0) + (get(stat, 'cbt_rooms') || 0),
  seats: (get(stat, 'seats') || 0) + (get(stat, 'special_seats') || 0) + (get(stat, 'cbt_seats') || 0),
});

// จับคู่สนามข้ามปีด้วยรหัสโรงเรียน เพราะ site_code อาจเปลี่ยนเมื่อลบสนามแล้วเพิ่มใหม่
export const siteKey = (site) => site.site_school_code || site.site_code;

/**
 * สถิติปี 69 ของวันสอบหมายเลข `day` รวมตามรหัสโรงเรียน
 * - สนาม PBT + สนาม CBT ของโรงเรียน/มหาวิทยาลัยเดียวกัน (รหัสโรงเรียนซ้ำ) รวมเป็นรายการเดียว
 *   เพราะปีนี้ไม่มี CBT แล้ว ต้องเอาที่นั่ง CBT มารวมกับปกติ
 * - สนาม CBT ที่ไม่เหลือข้อมูลห้องรายวันเลย ใช้ห้องต้นแบบ (stats) แทน และติด fromStats
 * คืน object key = รหัสโรงเรียน → { rooms, seats, cbtRooms, cbtSeats, site, hasCbt, cbtOnly, fromStats }
 */
export const buildHistoryDayBySchool = (historySites, day) => {
  const bySchool = {};
  (historySites || []).forEach((site) => {
    const key = siteKey(site);
    const isCbt = site.exam_type === 'cbt';
    const hasAnyDayData = !_.isEmpty(site.info_by_days);
    const fromStats = isCbt && !hasAnyDayData;
    const stat = fromStats ? totalStat(site.stats) : totalStat(get(site, `info_by_days.day${day}`));
    if (!stat.rooms && !stat.seats) return;

    if (!bySchool[key]) {
      bySchool[key] = { rooms: 0, seats: 0, cbtRooms: 0, cbtSeats: 0, site, hasCbt: false, cbtOnly: isCbt, fromStats: false };
    }
    const entry = bySchool[key];
    entry.rooms += stat.rooms;
    entry.seats += stat.seats;
    if (isCbt) {
      entry.hasCbt = true;
      entry.cbtRooms += stat.rooms;
      entry.cbtSeats += stat.seats;
      if (fromStats) entry.fromStats = true;
    } else {
      entry.site = site; // ใช้ชื่อ/รหัสของสนาม PBT เป็นหลักเมื่อมีทั้งสองแบบ
      entry.cbtOnly = false;
    }
  });
  return bySchool;
};

// รวมยอดของวัน พร้อมส่วนที่เป็น CBT (นับรวมอยู่ใน rooms/seats แล้ว) เพื่อบอกให้รู้ว่ารวม CBT มาเท่าไหร่
export const sumDayRows = (rows) =>
  rows.reduce(
    (acc, stat) => ({
      sites: acc.sites + 1,
      rooms: acc.rooms + stat.rooms,
      seats: acc.seats + stat.seats,
      cbt_sites: acc.cbt_sites + (stat.hasCbt ? 1 : 0),
      cbt_rooms: acc.cbt_rooms + (stat.cbtRooms || 0),
      cbt_seats: acc.cbt_seats + (stat.cbtSeats || 0),
    }),
    { sites: 0, rooms: 0, seats: 0, cbt_sites: 0, cbt_rooms: 0, cbt_seats: 0 },
  );

// สีของป้ายผลต่าง แยกตามทิศทาง (มากกว่า / น้อยกว่า / เท่าเดิม)
const DIFF_TONES = {
  up: { color: '#0b9c23', background: '#e9f7ec' },
  down: { color: '#e02020', background: '#fdecec' },
  flat: { color: '#979797', background: '#f2f2f3' },
};

// ป้ายให้สูงพอๆ กับตัวเลขที่อยู่ข้างหน้า ไม่ให้เด่นเกินตัวเลขจริง
const diffPillStyle = (tone) => ({
  display: 'inline-flex',
  alignItems: 'center',
  gap: 1,
  padding: '0 6px',
  borderRadius: 999,
  fontSize: '0.875em',
  lineHeight: 1.5,
  fontWeight: 600,
  whiteSpace: 'nowrap',
  ...DIFF_TONES[tone],
});

const ICON_STYLE = { fontSize: '0.85em' };

// ผลต่าง = ปีนี้ − ปี 69 (ลูกศรขึ้น/เขียว = ปีนี้มากกว่า, ลูกศรลง/แดง = ปีนี้น้อยกว่า)
export const DiffValue = ({ current, previous }) => {
  const diff = (current || 0) - (previous || 0);
  if (!diff) return <span style={diffPillStyle('flat')}>0</span>;
  const up = diff > 0;
  return (
    <Tooltip title={`ปีนี้${up ? 'มากกว่า' : 'น้อยกว่า'}ปี ${HISTORY_YEAR} อยู่ ${formatComma(Math.abs(diff))}`}>
      <span style={diffPillStyle(up ? 'up' : 'down')}>
        {up ? <CaretUpOutlined style={ICON_STYLE} /> : <CaretDownOutlined style={ICON_STYLE} />}
        {formatComma(Math.abs(diff))}
      </span>
    </Tooltip>
  );
};
